/* eslint-disable */
// src/services/visitMachineService.js

/*
 * Consultas auxiliares de máquinas/visitas (no el CRUD completo).
 * getCollectionPreviousValuesByLocal(localId): previos de recaudación para la pantalla.
 */

import axios from "services/axiosConfig";

const API_URL = "/api/visitMachine";

export function normalizeCollectionPreviousValueRow(row = {}) {
  const hasOwn = (key) => Object.prototype.hasOwnProperty.call(row, key);
  const preferPrev = (prevKey, currentKey) => {
    if (hasOwn(prevKey)) return row[prevKey];
    return row[currentKey] ?? null;
  };

  return {
    ...row,
    LocalMachineId: row.LocalMachineId ?? row.localMachineId ?? null,
    CreditsIn: preferPrev("PrevCreditsIn", "CreditsIn"),
    CreditsOut: preferPrev("PrevCreditsOut", "CreditsOut"),
    MoneyIn: preferPrev("PrevMoneyIn", "MoneyIn"),
    MoneyOut: preferPrev("PrevMoneyOut", "MoneyOut"),
    PrevCreditsIn: preferPrev("PrevCreditsIn", "CreditsIn"),
    PrevCreditsOut: preferPrev("PrevCreditsOut", "CreditsOut"),
    PrevMoneyIn: preferPrev("PrevMoneyIn", "MoneyIn"),
    PrevMoneyOut: preferPrev("PrevMoneyOut", "MoneyOut"),
    ...(hasOwn("PrevNetCreditsIn") ? { PrevNetCreditsIn: row.PrevNetCreditsIn } : {}),
    ...(hasOwn("PrevNetCreditsOut") ? { PrevNetCreditsOut: row.PrevNetCreditsOut } : {}),
    ...(hasOwn("PrevNetMoneyIn") ? { PrevNetMoneyIn: row.PrevNetMoneyIn } : {}),
    ...(hasOwn("PrevNetMoneyOut") ? { PrevNetMoneyOut: row.PrevNetMoneyOut } : {}),
    PrevVisitId: row.PrevVisitId ?? null,
    PrevVisitDate: row.PrevVisitDate ?? null,
    InstallationStartDateTime: row.InstallationStartDateTime ?? null,
    HasValidInitialValues: row.HasValidInitialValues === true,
    InitialValuesIn: row.InitialValuesIn ?? null,
    InitialValuesOut: row.InitialValuesOut ?? null,
    IsCollectibleInCurrentVisit: row.IsCollectibleInCurrentVisit === true,
    ShowInsertedInCurrentVisitNotice: row.ShowInsertedInCurrentVisitNotice === true,
  };
}

export function normalizeCollectionPreviousValueRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).map(normalizeCollectionPreviousValueRow);
}

export function getInitialValuesPerMachineErrorInfo(error) {
  const backendData = error?.response?.data || {};
  const backendCode = backendData?.code ?? null;
  const backendMessage =
    backendData?.error ??
    backendData?.message ??
    error?.message ??
    "No se pudieron guardar los valores iniciales.";

  const code = String(backendCode || "").trim();
  const message =
    String(backendMessage || "").trim() || "No se pudieron guardar los valores iniciales.";

  const normalizedMessage = (() => {
    switch (code) {
      case "LOCAL_MACHINE_NOT_PENDING_INITIAL_VALUES":
        return "La máquina ya no está pendiente de valores iniciales.";
      case "INSTALLATION_ALREADY_HAS_INITIAL_VALUES":
        return "La máquina ya fue inicializada o la instalación ya tiene valores iniciales válidos.";
      case "LOCAL_MACHINE_NOT_ACTIVE":
        return "La máquina ya no está activa.";
      case "LOCAL_MACHINE_INVALID_TYPE":
        return "La máquina ya no es válida para iniciar ciclo.";
      case "LOCAL_MACHINE_INVALID_ACTIVE_CONFIGURATION":
        return "La configuración activa de la máquina ya no es válida para iniciar ciclo.";
      case "INITIAL_VALUES_EDIT_BLOCKED_BY_SUBSEQUENT_COLLECTIONS":
        return "Los valores iniciales ya no se pueden editar porque la instalación tiene recaudaciones posteriores reales.";
      case "INITIAL_VALUES_CREATE_BLOCKED_BY_SUBSEQUENT_COLLECTIONS":
        return "Los valores iniciales ya no se pueden crear porque la instalación tiene recaudaciones posteriores reales.";
      case "INITIAL_VALUES_EDIT_BLOCKED":
        return "Los valores iniciales ya no se pueden editar.";
      case "INITIAL_VALUES_NOT_FOUND":
        return "La instalacion no tiene valores iniciales editables.";
      case "INITIAL_VALUES_ROW_NOT_EDITABLE":
        return "La fila histórica de valores iniciales ya no está disponible para editar.";
      default:
        return message;
    }
  })();

  const shouldRefresh =
    code === "LOCAL_MACHINE_NOT_PENDING_INITIAL_VALUES" ||
    code === "INSTALLATION_ALREADY_HAS_INITIAL_VALUES" ||
    code === "LOCAL_MACHINE_NOT_ACTIVE" ||
    code === "LOCAL_MACHINE_INVALID_TYPE" ||
    code === "LOCAL_MACHINE_INVALID_ACTIVE_CONFIGURATION" ||
    code === "INITIAL_VALUES_EDIT_BLOCKED_BY_SUBSEQUENT_COLLECTIONS" ||
    code === "INITIAL_VALUES_CREATE_BLOCKED_BY_SUBSEQUENT_COLLECTIONS" ||
    code === "INITIAL_VALUES_EDIT_BLOCKED" ||
    code === "INITIAL_VALUES_NOT_FOUND" ||
    code === "INITIAL_VALUES_ROW_NOT_EDITABLE";

  return {
    code,
    message: normalizedMessage,
    shouldRefresh,
    status: error?.response?.status ?? null,
  };
}

class VisitMachineService {
  static async getCollectionPreviousValuesByLocal(localId, visitDateTime = null, options = {}) {
    try {
      const endpoint = `${API_URL}/last-by-local/${localId}`;
      const excludeVisitId = Number(options?.excludeVisitId);
      const params = {
        ...(visitDateTime ? { visitDateTime } : {}),
        ...(Number.isInteger(excludeVisitId) && excludeVisitId > 0 ? { excludeVisitId } : {}),
      };
      const response = await axios.get(endpoint, {
        params,
      });
      return normalizeCollectionPreviousValueRows(response.data);
    } catch (error) {
      console.error("Error al obtener los previos de recaudación del local:", error);
      throw error;
    }
  }

  static async getPendingInitialSlotMachinesByLocal(localId) {
    try {
      const endpoint = `${API_URL}/pending-initial-slots/${localId}`;
      const response = await axios.get(endpoint);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error("Error al obtener tragamonedas pendientes de valores iniciales:", error);
      return [];
    }
  }

  static async getInitialValuesManageableSlotMachinesByLocal(localId) {
    try {
      const endpoint = `${API_URL}/manageable-initial-slots/${localId}`;
      const response = await axios.get(endpoint);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error("Error al obtener tragamonedas manejables para valores iniciales:", error);
      return [];
    }
  }

  static async getInitialValuesForLocalMachine(localMachineId) {
    try {
      const endpoint = `${API_URL}/initial-values/local-machine/${localMachineId}`;
      const response = await axios.get(endpoint);
      return response.data || null;
    } catch (error) {
      console.error("Error al obtener estado de valores iniciales por máquina:", {
        url: `${API_URL}/initial-values/local-machine/${localMachineId}`,
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      throw error;
    }
  }

  static async createInitialValuesForLocalMachine(localMachineId, payload = {}) {
    try {
      const endpoint = `${API_URL}/initial-values/local-machine/${localMachineId}`;
      const requestBody = {
        PostResetStartIn: payload?.PostResetStartIn,
        PostResetStartOut: payload?.PostResetStartOut,
        Note: payload?.Note,
      };
      const response = await axios.post(endpoint, requestBody);
      return response.data;
    } catch (error) {
      console.error("Error al crear valores iniciales por máquina:", {
        url: `${API_URL}/initial-values/local-machine/${localMachineId}`,
        sent: {
          PostResetStartIn: payload?.PostResetStartIn,
          PostResetStartOut: payload?.PostResetStartOut,
          Note: payload?.Note,
        },
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      throw error;
    }
  }

  static async updateInitialValuesForLocalMachine(localMachineId, payload = {}) {
    try {
      const endpoint = `${API_URL}/initial-values/local-machine/${localMachineId}`;
      const requestBody = {
        PostResetStartIn: payload?.PostResetStartIn,
        PostResetStartOut: payload?.PostResetStartOut,
        Note: payload?.Note,
      };
      const response = await axios.put(endpoint, requestBody);
      return response.data;
    } catch (error) {
      console.error("Error al actualizar valores iniciales por máquina:", {
        url: `${API_URL}/initial-values/local-machine/${localMachineId}`,
        sent: {
          PostResetStartIn: payload?.PostResetStartIn,
          PostResetStartOut: payload?.PostResetStartOut,
          Note: payload?.Note,
        },
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      throw error;
    }
  }
}

export default VisitMachineService;
